<template>
  <div id="sitesForm">
    <v-overlay :value="loading" z-index="6">
      <v-progress-circular indeterminate size="64"/>
    </v-overlay>
    <v-row no-gutters justify="start">
      <v-col cols="9">
        <v-card class="pa-3">
          <v-card-title>
            <h4>{{$t($route.meta.breadcrumb[2].text)}}</h4>
          </v-card-title>
          <v-card-subtitle>
            <p>{{$t('sites.formMessage')}}</p>
          </v-card-subtitle>
          <v-card-text>
            <v-progress-linear v-if="isEdit && loadItem" color="primary" indeterminate/>
            <p v-if="isEdit && loadItem" class="text-center">{{$t('general.loading')}}</p>
            <v-form
              lazy-validation
              ref="form"
              v-model="valid"
              v-if="!loadItem || !isEdit"
            >
              <v-row>
                <v-col class="pb-0" cols="6">
                  <v-text-field
                    outlined
                    dense
                    :rules="rules.textRules"
                    v-model="editedItem.name"
                  >
                    <template v-slot:label>
                      <span>{{$t('general.name')}}</span><span class="error--text">&nbsp;*</span>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col class="pb-0" cols="6">
                  <v-text-field
                    outlined
                    dense
                    :label="$t('general.manager')"
                    :rules="rules.textNoReq"
                    v-model="editedItem.manager"
                  />
                </v-col>
                <v-col class="pb-0" cols="6">
                  <v-text-field
                    outlined
                    dense
                    :label="$t('general.email')"
                    :rules="rules.emailNoReq"
                    v-model="editedItem.email"
                  />
                </v-col>
                <v-col class="pb-0" cols="6">
                  <v-text-field
                    outlined
                    dense
                    :label="$t('general.phone')"
                    v-mask="'(###) ### - ####'"
                    v-model="editedItem.phone"
                  />
                </v-col>
                <v-col class="pb-0" cols="6">
                  <v-text-field
                    outlined
                    dense
                    :rules="rules.textRules"
                    v-model="editedItem.address.street"
                  >
                    <template v-slot:label>
                      <span>{{$t('address.street')}}</span><span class="error--text">&nbsp;*</span>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col class="pb-0" cols="6">
                  <v-text-field
                    outlined
                    dense
                    :rules="rules.noSpecialChars"
                    v-model="editedItem.address.street_number"
                  >
                    <template v-slot:label>
                      <span>{{$t('address.number')}}</span><span class="error--text">&nbsp;*</span>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col class="pb-0" cols="6">
                  <v-text-field
                    outlined
                    dense
                    :label="$t('address.suburb')"
                    :rules="rules.textRules"
                    v-model="editedItem.address.suburb"
                  >
                    <template v-slot:label>
                      <span>{{$t('address.suburb')}}</span><span class="error--text">&nbsp;*</span>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col class="pb-0" cols="6">
                  <v-text-field
                    outlined
                    dense
                    :label="$t('address.city')"
                    :rules="rules.textRules"
                    v-model="editedItem.address.city"
                  >
                    <template v-slot:label>
                      <span>{{$t('address.city')}}</span><span class="error--text">&nbsp;*</span>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col class="pb-0" cols="6">
                  <v-text-field
                    outlined
                    dense
                    :label="$t('address.state')"
                    :rules="rules.textRules"
                    v-model="editedItem.address.state"
                  >
                    <template v-slot:label>
                      <span>{{$t('address.state')}}</span><span class="error--text">&nbsp;*</span>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col class="pb-0" cols="6">
                  <v-autocomplete
                    outlined
                    dense
                    hide-details="auto"
                    :items="countries"
                    item-text="name"
                    item-value="id"
                    :label="$t('address.country')"
                    v-model="editedItem.address.country_id"
                    :rules="rules.required"
                  >
                    <template v-slot:label>
                      <span>{{$t('address.country')}}</span><span class="error--text">&nbsp;*</span>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col class="pb-0" cols="6">
                  <v-text-field
                    outlined
                    dense
                    :label="$t('address.zip')"
                    :rules="rules.noSpecialChars"
                    v-model="editedItem.address.zip_code"
                  >
                    <template v-slot:label>
                      <span>{{$t('address.zip')}}</span><span class="error--text">&nbsp;*</span>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    outlined
                    dense
                    counter
                    rows="2"
                    :label="$t('address.additionalInfo')"
                    v-model="editedItem.additional_info"
                    :rules="rules.textNoReq"
                  />
                </v-col>
                <v-col cols="12" class="pb-0">
                  <h3>{{$t('general.geographic')}}</h3>
                </v-col>
                <v-col cols="6" class="pb-0">
                  <v-text-field
                    dense
                    hide-details="auto"
                    outlined
                    :rules="rules.coordinate"
                    :label="$t('general.latitude')"
                    v-model="editedItem.lat">
                  </v-text-field>
                </v-col>
                <v-col cols="6" class="pb-0">
                  <v-text-field
                    dense
                    outlined
                    hide-details="auto"
                    :rules="rules.coordinate"
                    :label="$t('general.longitude')"
                    v-model="editedItem.lng">
                  </v-text-field>
                </v-col>
                <v-col cols="6" class="pb-0">
                  <v-checkbox
                    dense
                    v-model="editedItem.external_inspections"
                    :label="$t('general.externalInspection')"
                  />
                </v-col>
                <v-col class="pt-0" cols="12">
                  <v-divider class="my-0"/>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="text-right">
                  <v-btn
                    class="mr-4"
                    color="primary"
                    outlined
                    @click="redirect"
                  >{{$t('general.close')}}</v-btn>
                  <v-btn
                    color="primary"
                    depressed
                    @click="add"
                  >{{$t('general.save')}}</v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import {mask} from 'vue-the-mask'
export default {
  name: 'sitesForm',
  directives: {mask},
  data () {
    return {
      loading: false,
      loadItem: true,
      valid: true,
      editedItem: {
        id: 0,
        name: '',
        lat: '',
        lng: '',
        external_inspections: false,
        address: {
          city: '',
          country: '',
          country_id: 150,
          state: '',
          suburb: '',
          street_number: '',
          zip_code: '',
          street: '',

        },
        country_id: '',
        manager: '',
        email: '',
        phone: '',
        additional_info: '',
      },
      countries: []
    }
  },
  created () {
    if (this.$route.params.id) {
      this.getItem(this.$route.params.id)
    }
    this.$store.dispatch('getCountry', {event: {context: this}})
  },
  computed: {
    isEdit () {
      if (this.$route.params.id) {
        return true
      } else {
        return false
      }
    },
    rules () {
      return this.$validationRules()
    }
  },
  methods: {
    add () {
      if (this.$refs.form.validate()) {
        this.loading = true
        if (this.isEdit) {
          this.put()
        } else {
          this.post()
        }
      }
    },
    put () {
      this.$store.dispatch('sites/put', {event: {item: this.editedItem, context: this}})
    },
    post () {
      this.$store.dispatch('sites/post', {event: {item: this.editedItem, context: this}})
    },
    getItem(id) {
      this.$store.dispatch('sites/getById', {event: {id: id, context: this}})
    },
    redirect () {
      this.$router.push('/sites')
    }
  }
}
</script>